import React, { useEffect, useRef, useCallback, useState } from 'react';
import Navbar from '../components/Navbar';
import Prism from '../components/Prism';
import filterImg from '../assets/images/noise-filter.png';
import ctaBgImg from '../assets/images/cta-bg.png';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const CURSOR_GLOW = 'radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.07) 40%, transparent 70%)';
const MOBILE_BREAKPOINT = 768;

const AboutAcademyPage: React.FC = () => {
  const heroSectionRef = useRef<HTMLElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef({ x: -300, y: -300 });
  const currentRef = useRef({ x: -300, y: -300 });
  const rafRef = useRef<number | null>(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);

  const onMouseMove = useCallback((e: MouseEvent) => {
    targetRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  useEffect(() => {
    const LERP = 0.07;
    const loop = () => {
      const cur = currentRef.current;
      const tgt = targetRef.current;
      cur.x += (tgt.x - cur.x) * LERP;
      cur.y += (tgt.y - cur.y) * LERP;
      if (glowRef.current) {
        glowRef.current.style.transform = `translate(${cur.x}px, ${cur.y}px)`;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onMouseMove]);

  const [section1Ref, section1Visible] = useScrollAnimation();
  const [section2Ref, section2Visible] = useScrollAnimation();
  const [section3Ref, section3Visible] = useScrollAnimation();
  const [section4Ref, section4Visible] = useScrollAnimation();
  const [section5Ref, section5Visible] = useScrollAnimation();
  const [section6Ref, section6Visible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.1 });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Responsive: use smaller Prism on mobile to avoid line artifacts and reduce size
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', check);
    check();
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ backgroundColor: '#0C0D12' }}>
      {/* Navbar */}
      <Navbar />

      {/* Cursor glow */}
      <div
        ref={glowRef}
        className="doctrine-cursor-glow"
        style={{ background: CURSOR_GLOW }}
        aria-hidden="true"
      />
      
      {/* Header/Hero Section */}
      <header ref={heroSectionRef} className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8 pt-16 md:pt-20">
        {/* Prism background — smaller/softer on mobile to avoid line artifacts */}
        <div className="absolute inset-0 overflow-hidden" style={{ width: '100%', height: '100%' }}>
          <div className={`absolute inset-0 w-full h-full prism-hero-wrapper ${isMobile ? 'prism-hero-mobile' : ''}`} style={{ transform: 'translateY(8%)' }}>
            <Prism
              animationType="rotate"
              timeScale={isMobile ? 0.2 : 0.3}
              height={isMobile ? 2.8 : 3.5}
              baseWidth={isMobile ? 4.2 : 5.5}
              scale={isMobile ? 2.4 : 3.6}
              hueShift={0}
              colorFrequency={1}
              noise={isMobile ? 0.15 : 0.25}
              glow={isMobile ? 0.65 : 1}
              suspendWhenOffscreen
            />
          </div>
          {/* Noise Filter Overlay */}
          <div 
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: `url(${filterImg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              mixBlendMode: 'overlay',
              opacity: 0.3,
              pointerEvents: 'none'
            }}
          ></div>
          {/* Dark Overlay for Better Contrast */}
          <div className="absolute inset-0 bg-black/30"></div>
          
          {/* Bottom Gradient Fade - Smooth transition to content section */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 md:h-40 lg:h-48"
            style={{
              background: 'linear-gradient(to top, rgba(12, 13, 18, 1) 0%, rgba(12, 13, 18, 0.8) 30%, rgba(12, 13, 18, 0.4) 70%, transparent 100%)',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          ></div>
        </div>
        
        {/* Title Overlay */}
        <div className="absolute inset-0 flex items-center justify-center text-center z-10">
          <div className="flex flex-col items-center justify-center space-y-4 sm:space-y-6">
            <h1 className="text-4xl font-bold sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight drop-shadow-[0_0_20px_rgba(255,248,220,0.9),0_0_40px_rgba(255,250,240,0.6)]" style={{ letterSpacing: '-0.01em' }}>
              About Academy
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 font-normal leading-relaxed drop-shadow-[0_0_10px_rgba(255,248,220,0.7),0_0_20px_rgba(255,250,240,0.4)]">
              An academy that does not teach belief,<br />
              but trains perception and systemic understanding.
            </p>
          </div>
        </div>
      </header>

      {/* Transition Element - Smooth fade between hero and content */}
      <div className="relative h-24 sm:h-32 md:h-40 lg:h-48 overflow-hidden" style={{ backgroundColor: '#0C0D12' }}>
        {/* Gradient fade overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(12, 13, 18, 0) 0%, rgba(12, 13, 18, 0.3) 30%, rgba(12, 13, 18, 0.7) 70%, rgba(12, 13, 18, 1) 100%)',
            zIndex: 1
          }}
        ></div>
      </div>
      
      {/* Main Content */}
      <div className="relative z-10">
        <div 
          className="absolute top-0 left-0 right-0 h-24 sm:h-32 md:h-40 lg:-mt-24 sm:-mt-32 md:-mt-40"
          style={{
            background: 'linear-gradient(to bottom, rgba(12, 13, 18, 1) 0%, rgba(12, 13, 18, 0.8) 30%, transparent 100%)',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
        <div className="doctrine-article-body about-page" style={{ position: 'relative', zIndex: 2 }}>
          <section ref={section1Ref as React.RefObject<HTMLElement>} className={`about-block fade-in-up ${section1Visible ? 'animate' : ''}`}>
            <h2 className="doctrine-section-title">The Academy</h2>
            <p className="about-lead">
              The Academy is not a school in the traditional sense.
            </p>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                It does not teach belief. It does not offer salvation.
                It does not promise transformation on a schedule.
              </p>
              <p className="doctrine-body-para about-standout about-quote">
                It exists for one purpose: <strong>to train perception.</strong>
              </p>
              <p className="doctrine-body-para">
                Most people are educated in skills, information, and compliance.
                Very few are taught how systems actually shape behavior, attention, and outcome.
                The Academy addresses that gap.
              </p>
            </div>
          </section>

          <section ref={section2Ref as React.RefObject<HTMLElement>} className={`about-block fade-in-up ${section2Visible ? 'animate' : ''}`}>
            <h2 className="doctrine-section-title">What This Is (and Isn&apos;t)</h2>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                This is not a religion. There is no worship, no doctrine to accept, no authority to submit to.
              </p>
              <p className="doctrine-body-para">
                This is not self-help. No affirmations, no motivational cycles, no promises of happiness.
                This is not performance. Nothing here is meant to be displayed, shared, or validated publicly.
              </p>
              <p className="doctrine-body-para about-standout about-quote">
                The Academy functions as a <strong>private study environment</strong> for those who want to understand structure before attempting control.
              </p>
            </div>
          </section>

          <section ref={section3Ref as React.RefObject<HTMLElement>} className={`about-block fade-in-up ${section3Visible ? 'animate' : ''}`}>
            <h2 className="doctrine-section-title">The Core Focus</h2>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                Members of the Academy study three domains:
              </p>
              <div className="about-domains">
                <div className="about-domain">
                  <span className="about-domain-name">Awareness</span>
                  <span className="about-domain-desc">learning to recognize patterns, loops, and invisible constraints</span>
                </div>
                <div className="about-domain">
                  <span className="about-domain-name">Control</span>
                  <span className="about-domain-desc">developing internal containment and deliberate placement of action</span>
                </div>
                <div className="about-domain">
                  <span className="about-domain-name">Freedom</span>
                  <span className="about-domain-desc">reducing reactivity so choice becomes real rather than automatic</span>
                </div>
              </div>
              <p className="doctrine-body-para">
                These are not stages of superiority. They are layers of orientation.
              </p>
              <p className="doctrine-body-para about-standout about-quote">
                Progress is not measured by belief or enthusiasm, but by <strong>consistency of perception</strong>.
              </p>
            </div>
          </section>

          <section ref={section4Ref as React.RefObject<HTMLElement>} className={`about-block fade-in-up ${section4Visible ? 'animate' : ''}`}>
            <h2 className="doctrine-section-title">How the Academy Operates</h2>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                The Academy is organized around a progression of texts, practices, and discussions known collectively as the <strong>Codex</strong>.
              </p>
              <p className="doctrine-body-para">
                Each volume reveals only what is necessary at that level. Nothing is rushed. Nothing is forced.
                Techniques are introduced gradually, and interpretation is deliberately withheld.
              </p>
              <p className="doctrine-body-para">
                This prevents dependency, fantasy, and inflation. Members are encouraged to observe, record, and question — not to imitate or exaggerate experience.
              </p>
            </div>
          </section>

          <section ref={section5Ref as React.RefObject<HTMLElement>} className={`about-block fade-in-up ${section5Visible ? 'animate' : ''}`}>
            <h2 className="doctrine-section-title">Who This Is For</h2>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                The Academy is for people who:
              </p>
              <ul className="about-list">
                <li>feel that effort alone doesn&apos;t explain outcomes</li>
                <li>notice repeating patterns in work, relationships, or internal states</li>
                <li>want clarity more than comfort</li>
                <li>prefer precision over mysticism-for-show</li>
              </ul>
              <p className="doctrine-body-para">
                It is not for those seeking identity, status, or specialness.
                Nothing here will tell you that you are chosen. Nothing here will flatter you.
              </p>
            </div>
          </section>

          <section ref={section6Ref as React.RefObject<HTMLElement>} className={`about-block fade-in-up ${section6Visible ? 'animate' : ''}`}>
            <h2 className="doctrine-section-title">Membership</h2>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                Membership is not about access to secrets.
                It is about <strong>capacity to handle ambiguity without rushing to meaning</strong>.
              </p>
              <p className="doctrine-body-para">
                Those who remain tend to share one trait: they are willing to sit with uncertainty long enough for structure to reveal itself.
              </p>
              <p className="doctrine-body-para about-standout about-quote">
                That quality cannot be taught. It can only be filtered for.
              </p>
            </div>
          </section>
        </div>

        {/* CTA Section - same as Landing page */}
        <section
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`relative min-h-[65vh] sm:min-h-[75vh] flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 fade-in-up ${ctaVisible ? 'animate' : ''}`}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${ctaBgImg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(4px)',
              WebkitFilter: 'blur(4px)'
            }}
          />
          {/* Noise Filter Overlay */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              backgroundImage: `url(${filterImg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              mixBlendMode: 'overlay',
              opacity: 0.6
            }}
          />
          {/* Top gradient fade */}
          <div
            className="absolute top-0 left-0 right-0 h-24 sm:h-32 md:h-40 lg:h-48 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, rgba(12,13,18,1) 0%, rgba(12,13,18,0.8) 30%, rgba(12,13,18,0.4) 70%, transparent 100%)',
              zIndex: 1
            }}
          />
          {/* Bottom gradient fade */}
          <div
            className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 md:h-40 lg:h-48 pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(12,13,18,1) 0%, rgba(12,13,18,0.8) 30%, rgba(12,13,18,0.4) 70%, transparent 100%)',
              zIndex: 1
            }}
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/40" />
          {/* Content */}
          <div className="relative z-10 max-w-4xl mx-auto text-center cta-container">
            <div className="space-y-4 sm:space-y-6 md:space-y-8">
              <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-amber-100 leading-relaxed font-medium" style={{ letterSpacing: '-0.01em' }}>
                Ready to train perception?
              </p>
              <p className="cta-text text-xl font-normal sm:text-2xl md:text-3xl text-amber-50/90 leading-relaxed">
                Join the Academy and sit with uncertainty until structure reveals itself.
              </p>
            </div>
            <div className="mt-8 sm:mt-12 md:mt-16">
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent mx-auto" />
            </div>
            <div className="mt-6 sm:mt-8 md:mt-12">
              <a
                href="/#pricing"
                className="cta-button inline-block px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base tracking-wider font-mono"
              >
                Join Inner Circle
              </a>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12 px-4 sm:px-6 lg:px-8">
          {/* Academy Info */}
          <div className="text-center lg:text-left lg:col-span-2">
            <h3 className="jacquard-font text-3xl sm:text-4xl mb-4">Order of Architects</h3>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-4 max-w-md lg:max-w-lg mx-auto lg:mx-0">
              We are Architects of causality, seeing the invisible chains of reality and placing new ones by choice.
            </p>
          </div>

          {/* Quick Links */}
          <div className="text-center lg:text-left">
            <h4 className="text-white font-semibold mb-4 text-lg">Resources</h4>
            <ul className="space-y-2 text-sm sm:text-base">
              <li><a href="/doctrine" className="text-gray-400 hover:text-white transition-colors duration-300">Doctrine</a></li>
              <li><a href="/lore" className="text-gray-400 hover:text-white transition-colors duration-300">Lore</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Ritual</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Archives</a></li>
            </ul>
          </div>

          {/* Discord CTA */}
          <div className="text-center lg:text-left">
            <h4 className="text-white font-semibold mb-4 text-lg">Join the Community</h4>
            <p className="text-gray-400 text-sm sm:text-base mb-4">
              Connect with fellow Architects in our Discord server.
            </p>
            <a href="#" className="discord-button inline-flex items-center space-x-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.019 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
              </svg>
              <span>Join Discord</span>
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 sm:pt-12 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-gray-300 text-sm">© {new Date().getFullYear()} Order of Architects. All rights reserved.</p>
            <div className="flex items-center gap-4 sm:gap-6 text-sm">
              <a href="#" className="text-gray-300 hover:text-white transition-colors duration-300">Privacy Policy</a>
              <a href="#" className="text-gray-300 hover:text-white transition-colors duration-300">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutAcademyPage;
